/// <amd-module name="Artifacts/FeedPicker/containers/FeedPickerContainer" />

import { FeedPicker } from "Artifacts/FeedPicker/components/FeedPicker";
import { getFeedPickerUnselectableRanges } from "Artifacts/FeedPicker/helpers/SelectionHelper";
import { FeedPickerModule, IFeedPickerModuleState } from "Artifacts/FeedPicker/redux";
import * as R from "Artifacts/FeedPicker/Resources";
import { ComponentState } from "Artifacts/Root/Constants";
import { isReady } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { connectDynamicModules } from "Artifacts/Root/Redux";
import { feedHubRoute } from "Artifacts/Root/routing/Constants";
import { IFeedIdentity } from "Artifacts/Root/types";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import { IDropdownPivot } from "VSSUI/Dropdown";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn } from "VSSUI/Table";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";

interface IFeedPickerContainerProps {
  currentFeed?: IFeedIdentity;
}

const GroupId = Object.freeze({
  Project: R.FeedPicker_ProjectGroupId,
  Collection: R.FeedPicker_CollectionGroupId,
});

const Actions = {};

const groups = [
  { id: GroupId.Project, name: R.FeedPicker_ProjectGroupName },
  { id: GroupId.Collection, name: R.FeedPicker_CollectionGroupName },
];

function toListBoxItems(feeds: IFeedIdentity[]): IListBoxItem<IFeedIdentity>[] {
  return feeds.map((feed: IFeedIdentity) => ({
    id: feed.id,
    text: feed.name,
    data: feed,
    groupId: feed.project ? GroupId.Project : GroupId.Collection,
  }));
}

const loadingItem = {
  id: "loading",
  type: ListBoxItemType.Loading,
  render: (
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<IListBoxItem<any>>,
    tableItem: IListBoxItem<any>,
  ) => {
    return (
      <LoadingCell
        key={rowIndex}
        columnIndex={columnIndex}
        tableColumn={tableColumn}
        tableItem={tableItem}
      />
    );
  },
};

const FeedPickerContainer: React.FC<IFeedPickerContainerProps &
  IFeedPickerContainerMapToStateProps &
  typeof Actions> = ({ componentState, feeds, deletedFeeds, currentFeed }) => {
  const history = useHistory();
  const [selection] = useState<DropdownSelection>(() => new DropdownSelection());

  const feedsProvider = useMemo(
    () =>
      new GroupedItemProvider(
        isReady(componentState) ? toListBoxItems(feeds) : [loadingItem],
        groups,
        true,
      ),
    [feeds, componentState],
  );
  const deletedFeedsProvider = useMemo(
    () =>
      new GroupedItemProvider(
        isReady(componentState) ? toListBoxItems(deletedFeeds) : [loadingItem],
        groups,
        true,
      ),
    [deletedFeeds, componentState],
  );

  const pivots = useMemo(
    (): IDropdownPivot<IFeedIdentity>[] => [
      {
        id: "feeds",
        name: R.FeedPicker_ActiveFeedsPivotLabel,
        items: feedsProvider,
        filterPlaceholderText: R.FeedPicker_Placeholder,
        filteredNoResultsText: R.FeedPicker_NoResultsText,
        noItemsText: R.FeedPicker_NoItemsText,
        showFilterBox: true,
      },
      {
        id: "deletedFeeds",
        name: R.FeedPicker_DeletedFeedsPivotLabel,
        items: deletedFeedsProvider,
        filterPlaceholderText: R.FeedPicker_Placeholder,
        filteredNoResultsText: R.FeedPicker_NoResultsText,
        noItemsText: R.FeedPicker_NoItemsRecycleBinText,
        showFilterBox: true,
      },
    ],
    [feedsProvider, deletedFeedsProvider],
  );

  const [selectedPivot, setSelectedPivot] = useState<IDropdownPivot<IFeedIdentity>>(pivots[0]);
  const onPivotClicked = (pivot: IDropdownPivot<IFeedIdentity>) => {
    setSelectedPivot(pivot);
  };

  const provider = useMemo(() => {
    const provider = new AggregateItemProvider<IListBoxItem<IFeedIdentity>>();
    pivots.forEach((pivot: IDropdownPivot<IFeedIdentity>) => {
      provider.push(pivot.items);
    });
    return provider;
  }, [pivots]);

  const onSelect = useCallback(
    (_event, item: IListBoxItem<IFeedIdentity>) => {
      if (!item.data) {
        throw new Error("IListBoxItem missing feed object in the 'data' propety.");
      }
      history.replace(generatePath(feedHubRoute, { feedIdentifier: item.data.name }));
    },
    [history],
  );

  useEffect(() => {
    if (isReady(componentState) && currentFeed != null) {
      const feedIndex = provider.value.findIndex((value) => value.data?.id === currentFeed?.id);
      if (feedIndex !== -1) {
        // We need to clear unselectable ranges before selecting current feed. For whatever reason
        // first elements of a provider become unselectable after loading item is rendered, probably a component bug.
        selection.clearUnselectable();
        const activeList = toListBoxItems(feeds);
        const deletedList = toListBoxItems(deletedFeeds);
        selection.unselectableRanges = getFeedPickerUnselectableRanges([activeList, deletedList]);
        selection.select(feedIndex);
      }
    }
  }, [componentState, currentFeed, selection, provider, feeds, deletedFeeds]);

  return (
    <FeedPicker
      pivots={pivots}
      selectedPivot={selectedPivot}
      onPivotClicked={onPivotClicked}
      dropdownProvider={provider}
      componentState={componentState}
      onSelect={onSelect}
      selection={selection}
    />
  );
};

interface IFeedPickerContainerMapToStateProps {
  componentState: ComponentState;
  feeds: IFeedIdentity[];
  deletedFeeds: IFeedIdentity[];
}

function mapStateToProps(state: IFeedPickerModuleState): IFeedPickerContainerMapToStateProps {
  return {
    componentState: state.feedPicker.componentState,
    feeds: state.feedPicker.feeds,
    deletedFeeds: state.feedPicker.deletedFeeds,
  };
}

const ConnectedFeedPickerContainer = connectDynamicModules<IFeedPickerContainerProps>(
  connect(mapStateToProps, Actions)(FeedPickerContainer),
  [FeedPickerModule],
);

export { ConnectedFeedPickerContainer as FeedPickerContainer };
